import { alpha, type Theme } from '@mui/material/styles';
import type { CSSObject } from '@mui/material/styles';
import type { SxProps } from '@mui/material';

/** Grosor unificado: vertical y horizontal */
export const SCROLLBAR_SIZE = 4;

export type ScrollbarVariant = 'default' | 'onDark' | 'hidden';

const getTrackColor = (theme: Theme, variant: ScrollbarVariant): string => {
  if (variant === 'onDark') {
    return alpha(theme.palette.common.white, 0.08);
  }
  return theme.palette.mode === 'dark'
    ? alpha(theme.palette.common.white, 0.06)
    : alpha(theme.palette.grey[400], 0.12);
};

const getThumbColor = (theme: Theme, variant: ScrollbarVariant): string => {
  if (variant === 'onDark') {
    return alpha(theme.palette.common.white, 0.28);
  }
  return theme.palette.mode === 'dark'
    ? alpha(theme.palette.common.white, 0.22)
    : theme.palette.grey[400];
};

const getThumbHoverColor = (theme: Theme, variant: ScrollbarVariant): string => {
  if (variant === 'onDark') {
    return alpha(theme.palette.common.white, 0.42);
  }
  return theme.palette.mode === 'dark'
    ? alpha(theme.palette.common.white, 0.35)
    : theme.palette.grey[500];
};

/**
 * Selectores planos para MuiCssBaseline (GlobalStyles no ejecuta callbacks por selector).
 * Cubre scroll vertical y horizontal en todo el sistema.
 */
export const getCssBaselineScrollbarOverrides = (theme: Theme): Record<string, CSSObject> => {
  const track = getTrackColor(theme, 'default');
  const thumb = getThumbColor(theme, 'default');
  const thumbHover = getThumbHoverColor(theme, 'default');

  return {
    '*': {
      scrollbarWidth: 'thin',
      scrollbarColor: `${thumb} ${track}`,
    },
    '*::-webkit-scrollbar': {
      width: SCROLLBAR_SIZE,
      height: SCROLLBAR_SIZE,
      background: track,
    },
    '*::-webkit-scrollbar-track': {
      background: track,
    },
    '*::-webkit-scrollbar-track-piece': {
      background: track,
    },
    '*::-webkit-scrollbar-corner': {
      background: track,
    },
    '*::-webkit-scrollbar-thumb': {
      backgroundColor: thumb,
      borderRadius: SCROLLBAR_SIZE,
    },
    '*::-webkit-scrollbar-thumb:hover': {
      backgroundColor: thumbHover,
    },
  };
};

/** Estilos de scrollbar para sx prop o ScrollableBox */
export const subtleScrollbarSx = (
  theme: Theme,
  variant: ScrollbarVariant = 'default',
): SxProps<Theme> => {
  if (variant === 'hidden') {
    return {
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
    };
  }

  const track = getTrackColor(theme, variant);
  const thumb = getThumbColor(theme, variant);
  const thumbHover = getThumbHoverColor(theme, variant);

  return {
    scrollbarWidth: 'thin',
    scrollbarColor: `${thumb} ${track}`,
    '&::-webkit-scrollbar': {
      width: SCROLLBAR_SIZE,
      height: SCROLLBAR_SIZE,
      background: track,
    },
    '&::-webkit-scrollbar-track': {
      background: track,
    },
    '&::-webkit-scrollbar-track-piece': {
      background: track,
    },
    '&::-webkit-scrollbar-corner': {
      background: track,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: thumb,
      borderRadius: SCROLLBAR_SIZE,
      '&:hover': {
        backgroundColor: thumbHover,
      },
    },
  };
};
